import type {
  AutomationTypedFixtureSubstitution,
  AutomationVersionTestSuiteCase,
} from '@/types/automations.ts';

export type VersionTestCaseDraft = {
  bindingRequestKey?: string;
  id: string;
  name: string;
  description: string;
  fixture_id: string;
  required: boolean;
  intentText: string;
  assertionSuiteText: string;
};

export const EMPTY_SCHEDULER_TEST_INTENT = {
  schema_version: 'scheduler_test_intent.v1' as const,
  target_step_ids: [] as string[],
  fixture_substitutions: [] as AutomationTypedFixtureSubstitution[],
  expected_routes: [] as { conditional_step_id: string; condition_id: string }[],
  expected_failure_terminal_step_ids: [] as string[],
  effect_directives: [] as { step_id: string; disposition: 'forbid' | 'simulate' }[],
};

export const EMPTY_VERSION_TEST_SUITE_SNAPSHOT = {
  schema_version: 'automation_version_test_suite.v2' as const,
  cases: [],
};

const EMPTY_ASSERTION_SUITE = {
  schema_version: 'builder_test_assertion_suite.v1',
  assertions: [],
};

export const createVersionTestCaseDraft = (): VersionTestCaseDraft => ({
  id: '',
  name: '',
  description: '',
  fixture_id: '',
  required: true,
  intentText: JSON.stringify(EMPTY_SCHEDULER_TEST_INTENT, null, 2),
  assertionSuiteText: JSON.stringify(EMPTY_ASSERTION_SUITE, null, 2),
});

export const toVersionTestCaseDraft = (testCase: AutomationVersionTestSuiteCase): VersionTestCaseDraft => ({
  id: testCase.id,
  name: testCase.name,
  description: testCase.description ?? '',
  fixture_id: testCase.fixture_id,
  required: testCase.required !== false,
  intentText: JSON.stringify(testCase.intent, null, 2),
  assertionSuiteText: JSON.stringify(testCase.assertion_suite, null, 2),
});

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

function assertRecord(value: unknown, label: string): asserts value is Record<string, unknown> {
  if (!isRecord(value)) throw new Error(`${label} must be an object`);
}

const assertExactKeys = (value: Record<string, unknown>, allowed: readonly string[], label: string) => {
  const allowedKeys = new Set(allowed);
  const unexpected = Object.keys(value).find((key) => !allowedKeys.has(key));
  if (unexpected) throw new Error(`${label} contains unsupported field ${unexpected}`);
};

const retiredIntentFields = new Set([
  'replay_mode',
  'replay_strategy',
  'execution_bounds',
  'fresh_step_ids',
  'error_boundary_expectations',
  'source_run_id',
]);

const validateCase = (value: unknown): AutomationVersionTestSuiteCase => {
  assertRecord(value, 'version test case');
  assertExactKeys(
    value,
    ['id', 'name', 'description', 'fixture_id', 'intent', 'assertion_suite', 'required'],
    'version test case',
  );
  for (const field of ['id', 'name', 'fixture_id'] as const) {
    if (typeof value[field] !== 'string') {
      throw new Error(`version test case ${field} must be a string`);
    }
  }
  if (value.description !== undefined && typeof value.description !== 'string') {
    throw new Error('version test case description must be a string');
  }
  if (value.required !== undefined && typeof value.required !== 'boolean') {
    throw new Error('case required must be a boolean');
  }
  assertRecord(value.intent, 'case intent');
  assertRecord(value.assertion_suite, 'assertion_suite');
  const retiredIntentField = Object.keys(value.intent).find((field) => retiredIntentFields.has(field));
  if (retiredIntentField) {
    throw new Error(`case intent contains retired replay field ${retiredIntentField}`);
  }
  return value as unknown as AutomationVersionTestSuiteCase;
};

const validateVersionTestSuiteSnapshot = (snapshot: unknown) => {
  assertRecord(snapshot, 'version test suite');
  assertExactKeys(snapshot, ['schema_version', 'cases'], 'version test suite');
  if (snapshot.schema_version !== 'automation_version_test_suite.v2' || !Array.isArray(snapshot.cases)) {
    throw new Error('Expected an automation_version_test_suite.v2 snapshot');
  }
  return {
    schema_version: 'automation_version_test_suite.v2' as const,
    cases: snapshot.cases.map(validateCase),
  };
};

export const buildVersionTestSuiteSnapshot = (drafts: VersionTestCaseDraft[]) => (
  validateVersionTestSuiteSnapshot({
    schema_version: 'automation_version_test_suite.v2',
    cases: drafts.map((draft) => ({
      id: draft.id.trim(),
      name: draft.name.trim(),
      description: draft.description.trim(),
      fixture_id: draft.fixture_id,
      required: draft.required,
      intent: JSON.parse(draft.intentText) as unknown,
      assertion_suite: JSON.parse(draft.assertionSuiteText) as unknown,
    })),
  })
);

export const parseVersionTestSuiteSnapshot = (value: string) => (
  validateVersionTestSuiteSnapshot(JSON.parse(value) as unknown)
);

export const bindFixtureSubstitutions = (
  intentText: string,
  substitutions: AutomationTypedFixtureSubstitution[],
): string => {
  const currentIntent: unknown = JSON.parse(intentText || '{}');
  if (currentIntent === null || Array.isArray(currentIntent) || typeof currentIntent !== 'object') {
    throw new Error('Declarative scheduler intent must be a JSON object.');
  }
  return JSON.stringify({
    ...EMPTY_SCHEDULER_TEST_INTENT,
    ...currentIntent,
    fixture_substitutions: substitutions,
  }, null, 2);
};
